import React, { useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  Lock, ShieldCheck, Calendar as CalIcon, Repeat, Home, Building2, MapPin, Car as CarIcon, PawPrint, TreePine, ShoppingBag,
} from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api, formatPrice } from "@/lib/api";

const FREQUENCIES = [
  { id: "one-time", label: "One-time", desc: "Single cleaning" },
  { id: "weekly", label: "Weekly", desc: "Every 7 days · steady team" },
  { id: "biweekly", label: "Bi-weekly", desc: "Every 14 days · popular" },
];

const SQFT = { under_1500: 120, "1500_3500": 220, "3500_5000": 320, "5000_plus": 450 };
const SQFT_LABEL = { under_1500: "Under 1,500 sq ft", "1500_3500": "1,500 – 3,500 sq ft", "3500_5000": "3,500 – 5,000 sq ft", "5000_plus": "5,000+ sq ft" };
const SPACE_MULT = { office: 1.0, retail: 1.1, gym: 1.3, clinic: 1.4 };
const SPACE_LABEL = { office: "Office", retail: "Retail", gym: "Gym", clinic: "Clinic" };

// -----------------------------------------------------------------------------
// Car cleaning constants — must stay in lock-step with backend calc_price("car")
// -----------------------------------------------------------------------------
const CAR_VEHICLES = ["sedan", "suv", "truck", "van", "other"];
const CAR_VEHICLE_LABEL = { sedan: "Sedan", suv: "SUV", truck: "Truck", van: "Van", other: "Other" };
const CAR_SERVICES = [
  { id: "basic_wash",       name: "Basic Wash" },
  { id: "interior",         name: "Interior Cleaning" },
  { id: "exterior",         name: "Exterior Cleaning" },
  { id: "both",             name: "Interior + Exterior" },
  { id: "deep_interior",    name: "Deep Interior Cleaning" },
  { id: "full_detail",      name: "Full Detail" },
  { id: "mobile_detailing", name: "Mobile Car Detailing", customQuote: true },
];
const CAR_PRICING = {
  basic_wash:    { sedan: 49,  suv: 59,  truck: 69,  van: 69  },
  interior:      { sedan: 99,  suv: 119, truck: 139, van: 139 },
  exterior:      { sedan: 119, suv: 139, truck: 159, van: 159 },
  both:          { sedan: 120, suv: 145, truck: 150, van: 175 },
  deep_interior: { sedan: 125, suv: 150, truck: 155, van: 180 },
  full_detail:   { sedan: 199, suv: 249, truck: 299, van: 299 },
};
const CAR_ADDONS = [
  { id: "pet_hair",              label: "Pet-hair removal",          price: 35 },
  { id: "carpet",                label: "Carpet / seat shampoo",     price: 30 },
  { id: "odor",                  label: "Odor removal",              price: 35 },
  { id: "trunk",                 label: "Trunk deep clean",          price: 20 },
  { id: "mess",                  label: "Excessive dirt / mess",     price: 30 },
  { id: "deep_stain",            label: "Deep stain treatment",      price: 40 },
  { id: "wax_sealant",           label: "Wax / paint sealant",       price: 45 },
  { id: "vacuum",                label: "Full vacuum",               price: 15 },
  { id: "leather_conditioning",  label: "Leather conditioning",      price: 30 },
  { id: "tire_wheel",            label: "Tire & wheel cleaning",     price: 20 },
  { id: "engine_bay",            label: "Engine-bay cleaning",       price: 60 },
  { id: "headlight_restoration", label: "Headlight restoration",     price: 35 },
  { id: "mobile_travel",         label: "Mobile / travel fee",       price: 20 },
];
const CAR_ADDON_PRICE = Object.fromEntries(CAR_ADDONS.map((a) => [a.id, a.price]));

// -----------------------------------------------------------------------------
// Pet care constants — must stay in lock-step with backend calc_price("pet")
// -----------------------------------------------------------------------------
const PET_SERVICES = [
  { id: "dog_walking",   name: "Dog Walking",         durations: ["30", "60"] },
  { id: "drop_in",       name: "Drop-In Pet Visits",  durations: ["30", "60"] },
  { id: "feeding",       name: "Pet Feeding",         durations: null },
  { id: "waste_cleanup", name: "Pet Waste Cleanup",   durations: null },
  { id: "dog_sitting",   name: "Dog Sitting",         durations: null },
  { id: "overnight",     name: "Overnight Pet Care",  durations: null },
];
const PET_SERVICE_LABEL = Object.fromEntries(PET_SERVICES.map((s) => [s.id, s.name]));
const PET_BASE_PRICE = {
  dog_walking:   { "30": 29, "60": 49 },
  drop_in:       { "30": 29, "60": 49 },
  feeding:       18,
  waste_cleanup: 30,
  dog_sitting:   65,
  overnight:     89,
};
const PET_ADDONS = [
  { id: "extra_pet",       label: "Extra pet",                 price: 10 },
  { id: "medication",      label: "Medication administration", price: 8 },
  { id: "weekend",         label: "Weekend / holiday",         price: 15 },
  { id: "key_pickup",      label: "Key pickup / return",       price: 10 },
  { id: "extra_walk_time", label: "Additional walk time",      price: 15 },
  { id: "feeding",         label: "Feeding",                   price: 10 },
  { id: "waste_cleanup",   label: "Waste cleanup",             price: 15 },
  { id: "special_care",    label: "Special-care instructions", price: 12 },
];
const PET_ADDON_META = Object.fromEntries(PET_ADDONS.map((a) => [a.id, a]));

// Pet Care is always priced in USD on the backend. Force USD in every
// display within the Pet flow, regardless of the user's global currency
// preference, so customers see the same numbers as the backend calc_price.
const PET_CURRENCY = "USD";

// -----------------------------------------------------------------------------
// Outdoor cleaning constants — must stay in lock-step with backend calc_price("outdoor")
// -----------------------------------------------------------------------------
const OUTDOOR_SERVICES = [
  { id: "yard_cleanup",     name: "Yard Cleanup" },
  { id: "patio_outdoor",    name: "Patio / Outdoor Cleaning" },
  { id: "garage_cleaning",  name: "Garage Cleaning" },
  { id: "pressure_washing", name: "Pressure Washing" },
];
const OUTDOOR_SERVICE_LABEL = Object.fromEntries(OUTDOOR_SERVICES.map((s) => [s.id, s.name]));
const OUTDOOR_BASE_PRICE = {
  yard_cleanup:     { small: 65,  medium: 110, large: 165, xlarge: 250 },
  patio_outdoor:    { small: 75,  medium: 120, large: 180, xlarge: 260 },
  garage_cleaning:  { small: 95,  medium: 145, large: 195, xlarge: 265 },
  pressure_washing: { small: 95,  medium: 140, large: 220, xlarge: 350 },
};
const OUTDOOR_SIZE_LABELS = {
  yard_cleanup:     { small: "Small · under 1/8 acre", medium: "Medium · 1/8–1/4 acre", large: "Large · 1/4–1/2 acre", xlarge: "XL · 1/2 acre+" },
  patio_outdoor:    { small: "Small patio",             medium: "Medium patio",          large: "Large patio",          xlarge: "XL deck / multi-area" },
  garage_cleaning:  { small: "1-car garage",            medium: "2-car garage",          large: "3-car garage",         xlarge: "4+ car / workshop" },
  pressure_washing: { small: "Driveway only",           medium: "Deck / patio",          large: "House soft-wash",      xlarge: "Full property" },
};
const OUTDOOR_ADDONS = [
  { id: "haul_away",           label: "Debris haul-away",         price: 40 },
  { id: "weeding",             label: "Weeding",                  price: 35 },
  { id: "gutter_clearing",     label: "Gutter clearing",          price: 60 },
  { id: "furniture_reset",     label: "Furniture reset",          price: 25 },
  { id: "chemical_treatment",  label: "Chemical treatment",       price: 30 },
  { id: "extra_area",          label: "Extra area / larger yard", price: 45 },
  { id: "patio_addon",         label: "Patio cleaning",           price: 35 },
  { id: "driveway_addon",      label: "Driveway cleaning",        price: 40 },
  { id: "garage_addon",        label: "Garage cleaning",          price: 45 },
  { id: "pressure_wash_addon", label: "Pressure washing",         price: 55 },
  { id: "trash_removal",       label: "Trash / debris removal",   price: 25 },
  { id: "outdoor_furniture",   label: "Outdoor furniture clean",  price: 30 },
  { id: "walkway",             label: "Walkway cleaning",         price: 25 },
  { id: "weekend",             label: "Weekend / holiday",        price: 15 },
  { id: "extra_labor",         label: "Extra labor / time",       price: 40 },
];
const OUTDOOR_ADDON_META = Object.fromEntries(OUTDOOR_ADDONS.map((a) => [a.id, a]));
const OUTDOOR_CURRENCY = "USD";

// -----------------------------------------------------------------------------
// Convenience services constants — must stay in lock-step with backend calc_price("convenience")
// -----------------------------------------------------------------------------
const CONV_SERVICES = [
  { id: "laundry",    name: "Laundry" },
  { id: "organizing", name: "Home Organizing" },
  { id: "errands",    name: "Errand Services" },
];
const CONV_SERVICE_LABEL = Object.fromEntries(CONV_SERVICES.map((s) => [s.id, s.name]));
const CONV_BASE_PRICE = {
  laundry:    { small: 30,  medium: 55,  large: 75,  xlarge: 95  },
  organizing: { small: 110, medium: 200, large: 280, xlarge: 350 },
  errands:    { small: 35,  medium: 65,  large: 90,  xlarge: 115 },
};
const CONV_SIZE_LABELS = {
  laundry:    { small: "1 load",           medium: "2 loads",         large: "3 loads",           xlarge: "4+ loads"       },
  organizing: { small: "2-hour session",   medium: "4-hour session",  large: "6-hour session",    xlarge: "Full 8-hour day"},
  errands:    { small: "Up to 1 hour",     medium: "Up to 2 hours",   large: "Up to 3 hours",     xlarge: "Up to 4 hours"  },
};
const CONV_ADDONS = [
  { id: "express",             label: "Express service",       price: 20 },
  { id: "weekend",             label: "Weekend / holiday",     price: 15 },
  { id: "eco_supplies",        label: "Eco-friendly supplies", price: 10 },
  { id: "multi_stop",          label: "Multiple stops",        price: 15 },
  { id: "extra_hour",          label: "Extra hour",            price: 30 },
  { id: "laundry_supplies",    label: "Laundry supplies",      price: 8 },
  { id: "organizing_supplies", label: "Organizing supplies",   price: 15 },
  { id: "key_pickup",          label: "Key pickup / return",   price: 10 },
];
const CONV_ADDON_META = Object.fromEntries(CONV_ADDONS.map((a) => [a.id, a]));
// -----------------------------------------------------------------------------
// CLEANBAZ Add-on Catalog — mirrors backend ADDON_CATALOG in server.py.
// Central source of truth for prices + labels. Change here to reprice.
// -----------------------------------------------------------------------------
const ADDON_CATALOG = {
  home: [
    { id: "fridge",             label: "Inside fridge",            price: 45 },
    { id: "oven",               label: "Inside oven",              price: 45 },
    { id: "interior_windows",   label: "Interior windows",         price: 35 },
    { id: "laundry",            label: "Laundry (wash & fold)",    price: 25 },
    { id: "cabinets",           label: "Inside cabinets",          price: 40 },
    { id: "baseboards",         label: "Baseboards",               price: 30 },
    { id: "inside_closets",     label: "Inside closets",           price: 35 },
    { id: "pet_hair",           label: "Pet-hair treatment",       price: 25 },
    { id: "extra_bedroom",      label: "Extra bedroom",            price: 40 },
    { id: "extra_bathroom",     label: "Extra bathroom",           price: 20 },
    { id: "deep_clean_upgrade", label: "Deep-clean upgrade",       price: 60 },
  ],
  business: [
    { id: "interior_windows",  label: "Interior windows",         price: 55 },
    { id: "floor_care",        label: "Floor care",               price: 75 },
    { id: "carpet_cleaning",   label: "Carpet cleaning",          price: 85 },
    { id: "restroom_deep",     label: "Restroom deep clean",      price: 55 },
    { id: "kitchen_breakroom", label: "Kitchen / breakroom",      price: 65 },
    { id: "trash_removal",     label: "Trash removal",            price: 25 },
    { id: "disinfecting",      label: "Disinfecting / sanitizing", price: 60 },
    { id: "high_touch",        label: "High-touch-area cleaning", price: 40 },
    { id: "after_hours",       label: "After-hours service",      price: 75 },
    { id: "post_construction", label: "Post-construction cleanup", price: 150 },
  ],
};
const HOME_ADDON_META = Object.fromEntries(ADDON_CATALOG.home.map((a) => [a.id, a]));
const BUSINESS_ADDON_META = Object.fromEntries(ADDON_CATALOG.business.map((a) => [a.id, a]));

const CONV_CURRENCY = "USD";

export default function Book() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const { user, currency, country } = useApp();

  // booking_type comes from the service catalog (/services) or the Landing
  // category cards. If the customer somehow lands on /book with no valid
  // booking_type, bounce them back to the catalog so they pick a service —
  // never silently default to Home Cleaning.
  const bookingTypeRaw = sp.get("booking_type");
  const VALID_BOOKING_TYPES = ["home", "business", "car", "pet", "outdoor", "convenience"];
  if (bookingTypeRaw != null && !VALID_BOOKING_TYPES.includes(bookingTypeRaw)) {
    // Unknown value → treat as missing.
  }
  const isMissingBookingType = !bookingTypeRaw;
  React.useEffect(() => {
    if (isMissingBookingType) nav("/services", { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMissingBookingType]);

  const bookingType =
    bookingTypeRaw === "business"    ? "business"    :
    bookingTypeRaw === "car"         ? "car"         :
    bookingTypeRaw === "pet"         ? "pet"         :
    bookingTypeRaw === "outdoor"     ? "outdoor"     :
    bookingTypeRaw === "convenience" ? "convenience" : "home";

  // ----- Home / business URL params (unchanged behaviour) -----
  const beds = parseInt(sp.get("beds") || "0");
  const baths = parseInt(sp.get("baths") || "0");
  const sqft = sp.get("sqft") || "under_1500";
  const spaceType = sp.get("space") || "office";
  const deep = sp.get("deep") === "1";
  const preselectHomeAddons = (sp.get("addons") || "").split(",").filter(Boolean);
  const [homeAddons, setHomeAddons] = useState(preselectHomeAddons);

  // ----- Car URL params (only used when bookingType === "car") -----
  const preselectVehicle = sp.get("vehicle") || "";
  const preselectService = sp.get("car_service") || "";
  const preselectCarAddons = (sp.get("car_addons") || "").split(",").filter(Boolean);

  const [carVehicle, setCarVehicle] = useState(
    CAR_VEHICLES.includes(preselectVehicle) ? preselectVehicle : ""
  );
  const [carService, setCarService] = useState(
    CAR_SERVICES.some((s) => s.id === preselectService) ? preselectService : ""
  );
  const [carAddons, setCarAddons] = useState(preselectCarAddons.filter((a) => a in CAR_ADDON_PRICE));

  // ----- Pet URL params (only used when bookingType === "pet") -----
  const preselectPetService = sp.get("pet_service") || "";
  const preselectPetCount = parseInt(sp.get("pet_count") || "1");
  const preselectPetDuration = sp.get("pet_duration") || "30";
  const preselectPetAddons = (sp.get("pet_addons") || "").split(",").filter(Boolean);

  const [petService, setPetService] = useState(
    PET_SERVICES.some((s) => s.id === preselectPetService) ? preselectPetService : ""
  );
  const [petCount, setPetCount] = useState(
    Number.isFinite(preselectPetCount) ? Math.max(1, Math.min(5, preselectPetCount)) : 1
  );
  const [petDuration, setPetDuration] = useState(
    ["30", "60"].includes(preselectPetDuration) ? preselectPetDuration : "30"
  );
  const [petAddons, setPetAddons] = useState(
    preselectPetAddons.filter((a) => a in PET_ADDON_META)
  );

  // ----- Outdoor URL params (only used when bookingType === "outdoor") -----
  const preselectOutdoorService = sp.get("outdoor_service") || "";
  const preselectOutdoorSize = sp.get("outdoor_size") || "medium";
  const preselectOutdoorAddons = (sp.get("outdoor_addons") || "").split(",").filter(Boolean);

  const [outdoorService, setOutdoorService] = useState(
    OUTDOOR_SERVICES.some((s) => s.id === preselectOutdoorService) ? preselectOutdoorService : ""
  );
  const [outdoorSize, setOutdoorSize] = useState(
    ["small", "medium", "large", "xlarge"].includes(preselectOutdoorSize) ? preselectOutdoorSize : "medium"
  );
  const [outdoorAddons, setOutdoorAddons] = useState(
    preselectOutdoorAddons.filter((a) => a in OUTDOOR_ADDON_META)
  );

  // ----- Convenience URL params (only used when bookingType === "convenience") -----
  const preselectConvService = sp.get("convenience_service") || "";
  const preselectConvSize = sp.get("convenience_size") || "medium";
  const preselectConvAddons = (sp.get("convenience_addons") || "").split(",").filter(Boolean);

  const [convService, setConvService] = useState(
    CONV_SERVICES.some((s) => s.id === preselectConvService) ? preselectConvService : ""
  );
  const [convSize, setConvSize] = useState(
    ["small", "medium", "large", "xlarge"].includes(preselectConvSize) ? preselectConvSize : "medium"
  );
  const [convAddons, setConvAddons] = useState(
    preselectConvAddons.filter((a) => a in CONV_ADDON_META)
  );

  // ----- Shared service-location + schedule state -----
  const [street, setStreet] = useState("");
  const [unit, setUnit] = useState("");
  const [city, setCity] = useState("");
  const [stateField, setStateField] = useState("");
  const [postal, setPostal] = useState(sp.get("postal") || "");
  const [countryField, setCountryField] = useState((country || "US").toUpperCase());
  const [notes, setNotes] = useState("");
  const [frequency, setFrequency] = useState("one-time");
  const [scheduledAt, setScheduledAt] = useState(() => {
    const d = new Date(Date.now() + 2 * 86400000);
    return d.toISOString().slice(0, 16);
  });
  const [loading, setLoading] = useState(false);

  // Live price — branches by bookingType.
  const priceInfo = useMemo(() => {
    if (bookingType === "convenience") {
      if (!convService) return { total: 0, quote: true, reason: "Pick a service" };
      const base = CONV_BASE_PRICE[convService]?.[convSize];
      if (base == null) return { total: 0, quote: true, reason: "Not priced" };
      const addonSum = convAddons.reduce((sum, id) => sum + (CONV_ADDON_META[id]?.price || 0), 0);
      return { total: base + addonSum, base, addonSum, quote: false };
    }
    if (bookingType === "outdoor") {
      if (!outdoorService) return { total: 0, quote: true, reason: "Pick a service" };
      const base = OUTDOOR_BASE_PRICE[outdoorService]?.[outdoorSize];
      if (base == null) return { total: 0, quote: true, reason: "Not priced" };
      const addonSum = outdoorAddons.reduce((sum, id) => sum + (OUTDOOR_ADDON_META[id]?.price || 0), 0);
      return { total: base + addonSum, base, addonSum, quote: false };
    }
    if (bookingType === "pet") {
      if (!petService) return { total: 0, quote: true, reason: "Pick a service" };
      const entry = PET_BASE_PRICE[petService];
      const svcDef = PET_SERVICES.find((s) => s.id === petService);
      const hasDur = !!svcDef?.durations;
      const base = typeof entry === "object" ? entry[hasDur ? petDuration : "30"] : entry;
      if (base == null) return { total: 0, quote: true, reason: "Not priced" };
      let addonSum = 0;
      petAddons.forEach((id) => {
        const meta = PET_ADDON_META[id];
        if (!meta) return;
        if (id === "extra_pet") {
          const extras = Math.max(0, petCount - 1);
          addonSum += meta.price * extras;
        } else {
          addonSum += meta.price;
        }
      });
      return { total: base + addonSum, base, addonSum, quote: false };
    }
    if (bookingType === "car") {
      // Custom-quote cases → no automatic price.
      if (!carVehicle || !carService) return { total: 0, quote: true, reason: "Pick vehicle + service" };
      const svc = CAR_SERVICES.find((s) => s.id === carService);
      if (svc?.customQuote || carVehicle === "other") return { total: 0, quote: true, reason: "Custom quote required — Mobile Detailing / Other vehicle types" };
      const table = CAR_PRICING[carService];
      const base = table?.[carVehicle];
      if (base == null) return { total: 0, quote: true, reason: "Not priced" };
      const addonSum = carAddons.reduce((s, id) => s + (CAR_ADDON_PRICE[id] || 0), 0);
      return { total: base + addonSum, base, addonSum, quote: false };
    }
    let subtotal;
    if (bookingType === "business") {
      subtotal = (SQFT[sqft] || 120) * (SPACE_MULT[spaceType] || 1.0);
    } else {
      subtotal = 65 + 40 * beds + 20 * baths;
    }
    if (deep) subtotal *= 1.5;
    // Home / business — use central catalog per booking type. Unknown ids
    // fall back to $35 (legacy behaviour) so old bookings still price cleanly.
    const catalogKey = bookingType === "business" ? "business" : "home";
    const meta = catalogKey === "business" ? BUSINESS_ADDON_META : HOME_ADDON_META;
    const addonFee = homeAddons.reduce((sum, id) => sum + (meta[id]?.price ?? 35), 0);
    return { total: subtotal + addonFee, quote: false };
  }, [bookingType, beds, baths, sqft, spaceType, deep, homeAddons, carVehicle, carService, carAddons, petService, petDuration, petCount, petAddons, outdoorService, outdoorSize, outdoorAddons, convService, convSize, convAddons]);

  const price = priceInfo.total;

  const toggleCarAddon = (id) => {
    setCarAddons((prev) => (prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]));
  };

  const togglePetAddon = (id) => {
    setPetAddons((prev) => (prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]));
  };

  const toggleOutdoorAddon = (id) => {
    setOutdoorAddons((prev) => (prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]));
  };

  const toggleHomeAddon = (id) => {
    setHomeAddons((prev) => (prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]));
  };

  const toggleConvAddon = (id) => {
    setConvAddons((prev) => (prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]));
  };

  const bumpPetCount = (delta) =>
    setPetCount((p) => Math.max(1, Math.min(5, p + delta)));

  const validateAddress = () => {
    if (!street.trim()) return "Street address is required.";
    if (!city.trim()) return "City is required.";
    if (!stateField.trim()) return "State / province is required.";
    if (!postal.trim()) return "ZIP / postal code is required.";
    if (!countryField.trim()) return "Country is required.";
    return null;
  };

  const validateCarSelection = () => {
    if (bookingType !== "car") return null;
    if (!carVehicle) return "Please pick a vehicle type.";
    if (!carService) return "Please pick a car cleaning service.";
    const svc = CAR_SERVICES.find((s) => s.id === carService);
    if (svc?.customQuote) return "Mobile Car Detailing requires a custom quote — booking opens in a later release.";
    if (carVehicle === "other") return '"Other" vehicles need a custom quote — please contact CLEANBAZ.';
    return null;
  };

  const validatePetSelection = () => {
    if (bookingType !== "pet") return null;
    if (!petService) return "Please pick a pet care service.";
    return null;
  };

  const validateOutdoorSelection = () => {
    if (bookingType !== "outdoor") return null;
    if (!outdoorService) return "Please pick an outdoor cleaning service.";
    if (!["small", "medium", "large", "xlarge"].includes(outdoorSize)) return "Please pick a size.";
    return null;
  };

  const validateConvSelection = () => {
    if (bookingType !== "convenience") return null;
    if (!convService) return "Please pick a convenience service.";
    if (!["small", "medium", "large", "xlarge"].includes(convSize)) return "Please pick a size.";
    return null;
  };

  const handleCheckout = async () => {
    if (!user) return nav("/login?next=" + encodeURIComponent(window.location.pathname + window.location.search));
    if (user.role !== "customer") { toast.error("Only clients can book"); return; }
    const carErr = validateCarSelection();
    if (carErr) { toast.error(carErr); return; }
    const petErr = validatePetSelection();
    if (petErr) { toast.error(petErr); return; }
    const outdoorErr = validateOutdoorSelection();
    if (outdoorErr) { toast.error(outdoorErr); return; }
    const convErr = validateConvSelection();
    if (convErr) { toast.error(convErr); return; }
    const err = validateAddress();
    if (err) { toast.error(err); return; }
    setLoading(true);
    try {
      const singleLine = `${street.trim()}${unit.trim() ? `, ${unit.trim()}` : ""}, ${city.trim()}, ${stateField.trim()} ${postal.trim()}, ${countryField.trim().toUpperCase()}`;
      const payload = {
        country: countryField.trim().toUpperCase(),
        postal_code: postal.trim(),
        street_address: street.trim(),
        unit: unit.trim(),
        city: city.trim(),
        state: stateField.trim(),
        address: singleLine,
        scheduled_at: new Date(scheduledAt).toISOString(),
        currency: (bookingType === "pet" || bookingType === "outdoor" || bookingType === "convenience") ? "USD" : currency,
        notes, frequency, booking_type: bookingType,
      };
      if (bookingType === "home") {
        payload.bedrooms = beds; payload.bathrooms = baths;
        payload.deep_clean = deep; payload.addons = homeAddons;
      } else if (bookingType === "business") {
        payload.sqft_bracket = sqft; payload.space_type = spaceType;
        payload.deep_clean = deep; payload.addons = homeAddons;
      } else if (bookingType === "car") {
        // car
        payload.car_vehicle_type = carVehicle;
        payload.car_service = carService;
        payload.addons = carAddons;
        payload.deep_clean = false;
        // Car cleaner drives to customer — same transportation-gated flow as home
        payload.requires_transportation = true;
        // Car cleaning is inherently one-off; force one-time.
        payload.frequency = "one-time";
      } else if (bookingType === "pet") {
        // pet
        payload.pet_service = petService;
        payload.pet_count = petCount;
        const svcDef = PET_SERVICES.find((s) => s.id === petService);
        payload.pet_duration = svcDef?.durations ? petDuration : null;
        payload.addons = petAddons;
        payload.deep_clean = false;
        // Cleaner travels to owner's home — keep transportation gate on.
        payload.requires_transportation = true;
        // Pet visits are booked one at a time.
        payload.frequency = "one-time";
      } else if (bookingType === "outdoor") {
        // outdoor
        payload.outdoor_service = outdoorService;
        payload.outdoor_size = outdoorSize;
        payload.addons = outdoorAddons;
        payload.deep_clean = false;
        // Cleaner travels to service address — keep transportation gate on.
        payload.requires_transportation = true;
        // Outdoor is booked one at a time.
        payload.frequency = "one-time";
      } else if (bookingType === "convenience") {
        // convenience
        payload.convenience_service = convService;
        payload.convenience_size = convSize;
        payload.addons = convAddons;
        payload.deep_clean = false;
        // Cleaner travels to pickup / service address — keep transport gate on.
        payload.requires_transportation = true;
        // Convenience is booked one at a time.
        payload.frequency = "one-time";
      } else {
        // fallback (should not hit — bookingType is normalised above)
        payload.deep_clean = false;
      }
      const { data: job } = await api.post("/jobs", payload);
      const { data } = await api.post("/payments/checkout", {
        job_id: job.id, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Checkout failed");
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  const isCar = bookingType === "car";
  const isPet = bookingType === "pet";
  const isOutdoor = bookingType === "outdoor";
  const isConv = bookingType === "convenience";
  const carSvcLabel = CAR_SERVICES.find((s) => s.id === carService)?.name || "—";
  const petSvcLabel = PET_SERVICE_LABEL[petService] || "—";
  const petSvcDef = PET_SERVICES.find((s) => s.id === petService) || null;
  const petHasDurations = !!petSvcDef?.durations;
  const outdoorSvcLabel = OUTDOOR_SERVICE_LABEL[outdoorService] || "—";
  const outdoorSizeLabel = (outdoorService && OUTDOOR_SIZE_LABELS[outdoorService]?.[outdoorSize]) || "—";
  const convSvcLabel = CONV_SERVICE_LABEL[convService] || "—";
  const convSizeLabel = (convService && CONV_SIZE_LABELS[convService]?.[convSize]) || "—";
  // Unified accent — used for icons, labels, step headings.
  const accent =
    isCar ? { text: "text-amber-600", bg: "bg-amber-100", tag: "text-amber-400", border: "border-amber-500", soft: "bg-amber-50", strong: "text-amber-700", chip: "text-amber-300", ring: "border-amber-500 bg-amber-50" } :
    isPet ? { text: "text-rose-600",  bg: "bg-rose-100",  tag: "text-rose-400",  border: "border-rose-500",  soft: "bg-rose-50",  strong: "text-rose-700",  chip: "text-rose-300",  ring: "border-rose-500 bg-rose-50" } :
    isOutdoor ? { text: "text-emerald-700", bg: "bg-emerald-100", tag: "text-emerald-400", border: "border-emerald-500", soft: "bg-emerald-50", strong: "text-emerald-800", chip: "text-emerald-300", ring: "border-emerald-500 bg-emerald-50" } :
    isConv ? { text: "text-violet-700", bg: "bg-violet-100", tag: "text-violet-400", border: "border-violet-500", soft: "bg-violet-50", strong: "text-violet-800", chip: "text-violet-300", ring: "border-violet-500 bg-violet-50" } :
    bookingType === "business" ? { text: "text-blue-700", bg: "bg-blue-100", tag: "text-teal-400", border: "border-teal-500", soft: "bg-teal-50", strong: "text-teal-700", chip: "text-teal-400", ring: "border-teal-500 bg-teal-50" } :
                                  { text: "text-teal-600", bg: "bg-teal-100", tag: "text-teal-400", border: "border-teal-500", soft: "bg-teal-50", strong: "text-teal-700", chip: "text-teal-400", ring: "border-teal-500 bg-teal-50" };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="mb-8 flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${accent.bg} ${accent.text}`}>
          {isCar ? <CarIcon className="w-5 h-5" /> :
           isPet ? <PawPrint className="w-5 h-5" /> :
           isOutdoor ? <TreePine className="w-5 h-5" /> :
           isConv ? <ShoppingBag className="w-5 h-5" /> :
           bookingType === "business" ? <Building2 className="w-5 h-5" /> :
                                         <Home className="w-5 h-5" />}
        </div>
        <div>
          <div className={`text-xs font-mono uppercase tracking-widest ${accent.text}`}>
            Checkout · {
              isCar ? "Car Cleaning · Mobile" :
              isPet ? "Pet Care · In-home" :
              isOutdoor ? "Outdoor Cleaning · Exterior" :
              isConv ? "Convenience · Everyday Help" :
              bookingType === "business" ? "Commercial" : "Residential"
            }
          </div>
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl text-slate-900" data-testid="book-heading">
            Secure your booking
          </h1>
        </div>
      </div>

      <div className="grid lg:grid-cols-5 gap-8">
        <div className="lg:col-span-3">
          <Card className="border-slate-200">
            <CardContent className="p-6 space-y-5">

              {/* Car-specific selectors (only when isCar) */}
              {isCar && (
                <>
                  <div>
                    <Label className="flex items-center gap-2 text-slate-900 font-semibold">
                      <CarIcon className="w-4 h-4 text-amber-600" />Step 1 — Vehicle
                    </Label>
                    <div className="mt-2 grid grid-cols-3 sm:grid-cols-5 gap-2">
                      {CAR_VEHICLES.map((v) => (
                        <button
                          key={v}
                          type="button"
                          onClick={() => setCarVehicle(v)}
                          data-testid={`book-car-vehicle-${v}`}
                          className={`p-3 rounded-xl border-2 text-sm font-semibold transition-all ${
                            carVehicle === v
                              ? "border-amber-500 bg-amber-50 text-amber-800"
                              : "border-slate-200 hover:border-slate-300 bg-white text-slate-700"
                          }`}
                        >
                          {CAR_VEHICLE_LABEL[v]}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="pt-4 border-t border-slate-100">
                    <Label className="text-slate-900 font-semibold">Step 2 — Service</Label>
                    <div className="mt-2 grid sm:grid-cols-2 gap-2">
                      {CAR_SERVICES.map((s) => {
                        const active = carService === s.id;
                        const priceHint = carVehicle && carVehicle !== "other" && !s.customQuote && CAR_PRICING[s.id]?.[carVehicle];
                        return (
                          <button
                            key={s.id}
                            type="button"
                            onClick={() => setCarService(s.id)}
                            data-testid={`book-car-service-${s.id}`}
                            className={`text-left p-3 rounded-xl border-2 transition-all ${
                              active ? "border-amber-500 bg-amber-50" : "border-slate-200 hover:border-slate-300 bg-white"
                            }`}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <div className="text-sm font-semibold text-slate-900">{s.name}</div>
                              <div className="text-xs font-mono text-amber-700">
                                {s.customQuote ? "Custom quote" :
                                  priceHint ? formatPrice(priceHint, currency) :
                                  carVehicle === "other" ? "Custom quote" : ""}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="pt-4 border-t border-slate-100">
                    <Label className="text-slate-900 font-semibold">Step 3 — Add-ons (optional)</Label>
                    <div className="mt-2 grid sm:grid-cols-2 gap-2">
                      {CAR_ADDONS.map((a) => {
                        const active = carAddons.includes(a.id);
                        return (
                          <label
                            key={a.id}
                            data-testid={`book-car-addon-${a.id}`}
                            className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                              active ? "border-amber-500 bg-amber-50" : "border-slate-200 hover:border-slate-300"
                            }`}
                          >
                            <Checkbox
                              checked={active}
                              onCheckedChange={() => toggleCarAddon(a.id)}
                              data-testid={`book-car-addon-toggle-${a.id}`}
                              className="border-amber-500 data-[state=checked]:bg-amber-500"
                            />
                            <div className="flex-1 text-sm font-semibold text-slate-800">{a.label}</div>
                            <div className="text-xs font-mono text-amber-700">+{formatPrice(a.price, currency)}</div>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                  <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div className="text-xs text-amber-800">
                      <strong>Mobile service.</strong> Your CLEANBAZ Pro comes to the address below with everything they need.
                    </div>
                  </div>
                </>
              )}

              {/* Pet-specific selectors (only when isPet) */}
              {isPet && (
                <>
                  <div>
                    <Label className="flex items-center gap-2 text-slate-900 font-semibold">
                      <PawPrint className="w-4 h-4 text-rose-600" />Step 1 — Service
                    </Label>
                    <div className="mt-2 grid sm:grid-cols-2 gap-2">
                      {PET_SERVICES.map((s) => {
                        const active = petService === s.id;
                        const entry = PET_BASE_PRICE[s.id];
                        const priceHint = typeof entry === "object"
                          ? `From ${formatPrice(Math.min(...Object.values(entry)), PET_CURRENCY)}`
                          : formatPrice(entry, PET_CURRENCY);
                        return (
                          <button
                            key={s.id}
                            type="button"
                            onClick={() => setPetService(s.id)}
                            data-testid={`book-pet-service-${s.id}`}
                            className={`text-left p-3 rounded-xl border-2 transition-all ${
                              active ? "border-rose-500 bg-rose-50" : "border-slate-200 hover:border-slate-300 bg-white"
                            }`}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <div className="text-sm font-semibold text-slate-900">{s.name}</div>
                              <div className="text-xs font-mono text-rose-700">{priceHint}</div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {petHasDurations && (
                    <div className="pt-4 border-t border-slate-100">
                      <Label className="text-slate-900 font-semibold">Step 2 — Session length</Label>
                      <div className="mt-2 grid grid-cols-2 gap-2">
                        {petSvcDef.durations.map((d) => {
                          const active = petDuration === d;
                          return (
                            <button
                              key={d}
                              type="button"
                              onClick={() => setPetDuration(d)}
                              data-testid={`book-pet-duration-${d}`}
                              className={`p-3 rounded-xl border-2 text-sm font-semibold transition-all ${
                                active ? "border-rose-500 bg-rose-50 text-rose-800" : "border-slate-200 hover:border-slate-300 bg-white text-slate-700"
                              }`}
                            >
                              {d} minutes · {formatPrice(PET_BASE_PRICE[petService][d], PET_CURRENCY)}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div className="pt-4 border-t border-slate-100">
                    <Label className="text-slate-900 font-semibold">
                      {petHasDurations ? "Step 3" : "Step 2"} — Number of pets
                    </Label>
                    <div className="mt-2 flex items-center gap-3">
                      <Button
                        type="button"
                        onClick={() => bumpPetCount(-1)}
                        disabled={petCount <= 1}
                        data-testid="book-pet-count-decrement"
                        size="sm"
                        variant="outline"
                        className="w-9 h-9 p-0 rounded-full border-rose-300 text-rose-700"
                      >
                        −
                      </Button>
                      <div data-testid="book-pet-count-value" className="w-10 text-center font-display font-bold text-2xl text-slate-900">
                        {petCount}
                      </div>
                      <Button
                        type="button"
                        onClick={() => bumpPetCount(1)}
                        disabled={petCount >= 5}
                        data-testid="book-pet-count-increment"
                        size="sm"
                        variant="outline"
                        className="w-9 h-9 p-0 rounded-full border-rose-300 text-rose-700"
                      >
                        +
                      </Button>
                      <div className="text-xs text-slate-500">Extra-pet fee applies from the 2nd pet onward.</div>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-slate-100">
                    <Label className="text-slate-900 font-semibold">
                      {petHasDurations ? "Step 4" : "Step 3"} — Add-ons (optional)
                    </Label>
                    <div className="mt-2 grid sm:grid-cols-2 gap-2">
                      {PET_ADDONS.map((a) => {
                        const active = petAddons.includes(a.id);
                        return (
                          <label
                            key={a.id}
                            data-testid={`book-pet-addon-${a.id}`}
                            className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                              active ? "border-rose-500 bg-rose-50" : "border-slate-200 hover:border-slate-300"
                            }`}
                          >
                            <Checkbox
                              checked={active}
                              onCheckedChange={() => togglePetAddon(a.id)}
                              data-testid={`book-pet-addon-toggle-${a.id}`}
                              className="border-rose-500 data-[state=checked]:bg-rose-500"
                            />
                            <div className="flex-1 text-sm font-semibold text-slate-800">{a.label}</div>
                            <div className="text-xs font-mono text-rose-700">+{formatPrice(a.price, PET_CURRENCY)}</div>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                  <div className="rounded-xl bg-rose-50 border border-rose-200 p-3 flex items-start gap-2">
                    <PawPrint className="w-4 h-4 text-rose-600 mt-0.5 flex-shrink-0" />
                    <div className="text-xs text-rose-800">
                      <strong>In-home service.</strong> Your CLEANBAZ Pro comes to the address below at the scheduled time.
                    </div>
                  </div>
                </>
              )}

              {/* Outdoor-specific selectors (only when isOutdoor) */}
              {isOutdoor && (
                <>
                  <div>
                    <Label className="flex items-center gap-2 text-slate-900 font-semibold">
                      <TreePine className="w-4 h-4 text-emerald-700" />Step 1 — Service
                    </Label>
                    <div className="mt-2 grid sm:grid-cols-2 gap-2">
                      {OUTDOOR_SERVICES.map((s) => {
                        const active = outdoorService === s.id;
                        const min = Math.min(...Object.values(OUTDOOR_BASE_PRICE[s.id]));
                        return (
                          <button
                            key={s.id}
                            type="button"
                            onClick={() => setOutdoorService(s.id)}
                            data-testid={`book-outdoor-service-${s.id}`}
                            className={`text-left p-3 rounded-xl border-2 transition-all ${
                              active ? "border-emerald-500 bg-emerald-50" : "border-slate-200 hover:border-slate-300 bg-white"
                            }`}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <div className="text-sm font-semibold text-slate-900">{s.name}</div>
                              <div className="text-xs font-mono text-emerald-700">From {formatPrice(min, OUTDOOR_CURRENCY)}</div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {outdoorService && (
                    <div className="pt-4 border-t border-slate-100">
                      <Label className="text-slate-900 font-semibold">Step 2 — Size</Label>
                      <div className="mt-2 grid grid-cols-2 lg:grid-cols-4 gap-2">
                        {["small", "medium", "large", "xlarge"].map((sz) => {
                          const active = outdoorSize === sz;
                          return (
                            <button
                              key={sz}
                              type="button"
                              onClick={() => setOutdoorSize(sz)}
                              data-testid={`book-outdoor-size-${sz}`}
                              className={`p-3 rounded-xl border-2 text-left transition-all ${
                                active ? "border-emerald-500 bg-emerald-50" : "border-slate-200 hover:border-slate-300 bg-white"
                              }`}
                            >
                              <div className="text-sm font-semibold text-slate-900">{OUTDOOR_SIZE_LABELS[outdoorService][sz]}</div>
                              <div className="mt-0.5 text-xs font-mono text-emerald-700">{formatPrice(OUTDOOR_BASE_PRICE[outdoorService][sz], OUTDOOR_CURRENCY)}</div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div className="pt-4 border-t border-slate-100">
                    <Label className="text-slate-900 font-semibold">Step 3 — Add-ons (optional)</Label>
                    <div className="mt-2 grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                      {OUTDOOR_ADDONS.map((a) => {
                        const active = outdoorAddons.includes(a.id);
                        return (
                          <label
                            key={a.id}
                            data-testid={`book-outdoor-addon-${a.id}`}
                            className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                              active ? "border-emerald-500 bg-emerald-50" : "border-slate-200 hover:border-slate-300"
                            }`}
                          >
                            <Checkbox
                              checked={active}
                              onCheckedChange={() => toggleOutdoorAddon(a.id)}
                              data-testid={`book-outdoor-addon-toggle-${a.id}`}
                              className="border-emerald-500 data-[state=checked]:bg-emerald-500"
                            />
                            <div className="flex-1 text-sm font-semibold text-slate-800">{a.label}</div>
                            <div className="text-xs font-mono text-emerald-700">+{formatPrice(a.price, OUTDOOR_CURRENCY)}</div>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                  <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 flex items-start gap-2">
                    <TreePine className="w-4 h-4 text-emerald-700 mt-0.5 flex-shrink-0" />
                    <div className="text-xs text-emerald-800">
                      <strong>On-site service.</strong> Your CLEANBAZ Pro arrives at the address below at the scheduled time.
                    </div>
                  </div>
                </>
              )}

              {/* Convenience-specific selectors (only when isConv) */}
              {isConv && (
                <>
                  <div>
                    <Label className="flex items-center gap-2 text-slate-900 font-semibold">
                      <ShoppingBag className="w-4 h-4 text-violet-700" />Step 1 — Service
                    </Label>
                    <div className="mt-2 grid sm:grid-cols-3 gap-2">
                      {CONV_SERVICES.map((s) => {
                        const active = convService === s.id;
                        const min = Math.min(...Object.values(CONV_BASE_PRICE[s.id]));
                        return (
                          <button
                            key={s.id}
                            type="button"
                            onClick={() => setConvService(s.id)}
                            data-testid={`book-conv-service-${s.id}`}
                            className={`text-left p-3 rounded-xl border-2 transition-all ${
                              active ? "border-violet-500 bg-violet-50" : "border-slate-200 hover:border-slate-300 bg-white"
                            }`}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <div className="text-sm font-semibold text-slate-900">{s.name}</div>
                              <div className="text-xs font-mono text-violet-700">From {formatPrice(min, CONV_CURRENCY)}</div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {convService && (
                    <div className="pt-4 border-t border-slate-100">
                      <Label className="text-slate-900 font-semibold">
                        Step 2 — {convService === "laundry" ? "Load Count" : convService === "organizing" ? "Session Length" : "Time Window"}
                      </Label>
                      <div className="mt-2 grid grid-cols-2 lg:grid-cols-4 gap-2">
                        {["small", "medium", "large", "xlarge"].map((sz) => {
                          const active = convSize === sz;
                          return (
                            <button
                              key={sz}
                              type="button"
                              onClick={() => setConvSize(sz)}
                              data-testid={`book-conv-size-${sz}`}
                              className={`p-3 rounded-xl border-2 text-left transition-all ${
                                active ? "border-violet-500 bg-violet-50" : "border-slate-200 hover:border-slate-300 bg-white"
                              }`}
                            >
                              <div className="text-sm font-semibold text-slate-900">{CONV_SIZE_LABELS[convService][sz]}</div>
                              <div className="mt-0.5 text-xs font-mono text-violet-700">{formatPrice(CONV_BASE_PRICE[convService][sz], CONV_CURRENCY)}</div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div className="pt-4 border-t border-slate-100">
                    <Label className="text-slate-900 font-semibold">Step 3 — Add-ons (optional)</Label>
                    <div className="mt-2 grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                      {CONV_ADDONS.map((a) => {
                        const active = convAddons.includes(a.id);
                        return (
                          <label
                            key={a.id}
                            data-testid={`book-conv-addon-${a.id}`}
                            className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                              active ? "border-violet-500 bg-violet-50" : "border-slate-200 hover:border-slate-300"
                            }`}
                          >
                            <Checkbox
                              checked={active}
                              onCheckedChange={() => toggleConvAddon(a.id)}
                              data-testid={`book-conv-addon-toggle-${a.id}`}
                              className="border-violet-500 data-[state=checked]:bg-violet-500"
                            />
                            <div className="flex-1 text-sm font-semibold text-slate-800">{a.label}</div>
                            <div className="text-xs font-mono text-violet-700">+{formatPrice(a.price, CONV_CURRENCY)}</div>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                  <div className="rounded-xl bg-violet-50 border border-violet-200 p-3 flex items-start gap-2">
                    <ShoppingBag className="w-4 h-4 text-violet-700 mt-0.5 flex-shrink-0" />
                    <div className="text-xs text-violet-800">
                      <strong>Everyday help.</strong> Your CLEANBAZ Pro arrives at the pickup / service address below at the scheduled time.
                    </div>
                  </div>
                </>
              )}

              {/* Home / Commercial add-ons — visible only for those booking types.
                  Central catalog: change ADDON_CATALOG.home / .business to reprice. */}
              {(bookingType === "home" || bookingType === "business") && (
                <div className="pt-2 border-t border-slate-100">
                  <Label className="flex items-center gap-2 text-slate-900 font-semibold">
                    {bookingType === "home"
                      ? <Home className="w-4 h-4 text-teal-600" />
                      : <Building2 className="w-4 h-4 text-blue-700" />}
                    Optional Add-ons
                  </Label>
                  <div className="mt-2 grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {(bookingType === "home" ? ADDON_CATALOG.home : ADDON_CATALOG.business).map((a) => {
                      const active = homeAddons.includes(a.id);
                      const chip = bookingType === "home" ? "border-teal-500 bg-teal-50" : "border-blue-500 bg-blue-50";
                      const priceColor = bookingType === "home" ? "text-teal-700" : "text-blue-700";
                      return (
                        <label
                          key={a.id}
                          data-testid={`book-${bookingType === "home" ? "home" : "business"}-addon-${a.id}`}
                          className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                            active ? chip : "border-slate-200 hover:border-slate-300"
                          }`}
                        >
                          <Checkbox
                            checked={active}
                            onCheckedChange={() => toggleHomeAddon(a.id)}
                            data-testid={`book-${bookingType === "home" ? "home" : "business"}-addon-toggle-${a.id}`}
                            className={bookingType === "home" ? "border-teal-500 data-[state=checked]:bg-teal-500" : "border-blue-500 data-[state=checked]:bg-blue-500"}
                          />
                          <div className="flex-1 text-sm font-semibold text-slate-800">{a.label}</div>
                          <div className={`text-xs font-mono ${priceColor}`}>+{formatPrice(a.price, currency)}</div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Frequency — hide for car, pet, outdoor, and convenience (all one-time only) */}
              {!isCar && !isPet && !isOutdoor && !isConv && (
                <div>
                  <Label className="flex items-center gap-2"><Repeat className="w-4 h-4 text-teal-600" />Frequency</Label>
                  <div className="mt-2 grid grid-cols-3 gap-2">
                    {FREQUENCIES.map((f) => (
                      <button
                        key={f.id}
                        type="button"
                        onClick={() => setFrequency(f.id)}
                        data-testid={`book-freq-${f.id}`}
                        className={`text-left p-3 rounded-xl border-2 transition-all ${frequency === f.id ? "border-teal-500 bg-teal-50" : "border-slate-200 hover:border-slate-300 bg-white"}`}
                      >
                        <div className="text-sm font-semibold text-slate-900">{f.label}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{f.desc}</div>
                      </button>
                    ))}
                  </div>
                  {frequency !== "one-time" && (
                    <div className="mt-2 text-xs text-teal-700 bg-teal-50 p-2 rounded-lg">
                      Same cleaner, same day. Next occurrence auto-books after completion.
                    </div>
                  )}
                </div>
              )}

              {/* Structured service address */}
              <div className={`pt-2 ${isCar || isPet || isOutdoor || isConv ? "border-t border-slate-100" : "border-t border-slate-200"}`}>
                <Label className="flex items-center gap-2 text-slate-900 font-semibold">
                  <MapPin className={`w-4 h-4 ${accent.text}`} />
                  {isCar ? "Step 4 — Service Location" :
                   isPet ? `${petHasDurations ? "Step 5" : "Step 4"} — Service Location` :
                   isOutdoor ? "Step 4 — Service Location" :
                   isConv ? "Step 4 — Pickup / Service Location" :
                   "Service address"}
                </Label>
                <div className="mt-3 space-y-3">
                  <div>
                    <Label className="text-xs text-slate-500">Street address *</Label>
                    <Input value={street} onChange={(e) => setStreet(e.target.value)} data-testid="book-street" placeholder={isCar ? "Where should we park?" : isPet ? "123 Main Street" : isOutdoor ? "123 Main Street" : isConv ? "Pickup / service address" : bookingType === "business" ? "500 5th Avenue" : "123 Main Street"} className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Apartment / Unit / Suite</Label>
                    <Input value={unit} onChange={(e) => setUnit(e.target.value)} data-testid="book-unit" placeholder={isCar ? "Parking spot / lot # (optional)" : isPet ? "Apt / unit (optional)" : isOutdoor ? "Gate code / access (optional)" : isConv ? "Apt / suite (optional)" : "Apt 4B, Suite 200 (optional)"} className="mt-1" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-slate-500">City *</Label>
                      <Input value={city} onChange={(e) => setCity(e.target.value)} data-testid="book-city" placeholder="New York" className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">State *</Label>
                      <Input value={stateField} onChange={(e) => setStateField(e.target.value.toUpperCase())} data-testid="book-state" placeholder="NY" className="mt-1 uppercase" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-slate-500">ZIP / Postal Code *</Label>
                      <Input value={postal} onChange={(e) => setPostal(e.target.value)} data-testid="book-postal" placeholder="10001" className="mt-1 font-mono" />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Country *</Label>
                      <Input value={countryField} onChange={(e) => setCountryField(e.target.value.toUpperCase())} data-testid="book-country" placeholder="US" maxLength={2} className="mt-1 uppercase font-mono" />
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <Label className="flex items-center gap-2">
                  <CalIcon className={`w-4 h-4 ${accent.text}`} />
                  {isCar ? "Step 5 — Schedule" :
                   isPet ? `${petHasDurations ? "Step 6" : "Step 5"} — Schedule` :
                   isOutdoor ? "Step 5 — Schedule" :
                   isConv ? "Step 5 — Schedule" :
                   "Scheduled Date & Time"}
                </Label>
                <Input type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} data-testid="book-schedule" className="mt-1.5 font-mono" />
              </div>
              <div>
                <Label>Notes (optional)</Label>
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="book-notes" placeholder={
                  isCar ? "Interior car color, mess level, valet code..." :
                  isPet ? "Pet name, temperament, allergies, key location..." :
                  isOutdoor ? "Gate code, water source location, restrictions, HOA notes..." :
                  isConv ? "Detergent preferences, shopping list, stops, delivery notes..." :
                  bookingType === "business" ? "Access code, alarm instructions, restricted areas..." :
                                                "Gate code, pet warnings, focus areas..."
                } className="mt-1.5" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* -------- Order summary sidebar -------- */}
        <div className="lg:col-span-2">
          <Card className={`sticky top-24 text-white border-slate-900 bg-slate-900`}>
            <CardContent className="p-6">
              <div className={`text-xs font-mono uppercase tracking-widest ${accent.tag}`}>
                {isCar ? "Step 6 — Review & Checkout" :
                 isPet ? `${petHasDurations ? "Step 7" : "Step 6"} — Review & Checkout` :
                 isOutdoor ? "Step 6 — Review & Checkout" :
                 isConv ? "Step 6 — Review & Checkout" :
                 "Order Summary"}
              </div>
              <div className="mt-2 text-sm space-y-1.5 text-slate-300">
                {isCar ? (
                  <>
                    <div>
                      <span className="text-slate-500">Vehicle:</span>{" "}
                      <span data-testid="book-car-summary-vehicle" className="text-slate-100 font-semibold">
                        {carVehicle ? CAR_VEHICLE_LABEL[carVehicle] : "—"}
                      </span>
                    </div>
                    <div>
                      <span className="text-slate-500">Service:</span>{" "}
                      <span data-testid="book-car-summary-service" className="text-slate-100 font-semibold">{carSvcLabel}</span>
                    </div>
                    {carAddons.length > 0 && (
                      <div className="text-amber-300 font-mono text-xs" data-testid="book-car-summary-addons">
                        + {carAddons.map((id) => CAR_ADDONS.find((a) => a.id === id)?.label).filter(Boolean).join(", ")}
                      </div>
                    )}
                    <div className="text-amber-400 font-mono text-xs uppercase tracking-wider pt-1">Mobile · one-time</div>
                  </>
                ) : isPet ? (
                  <>
                    <div>
                      <span className="text-slate-500">Service:</span>{" "}
                      <span data-testid="book-pet-summary-service" className="text-slate-100 font-semibold">{petSvcLabel}</span>
                    </div>
                    {petHasDurations && (
                      <div>
                        <span className="text-slate-500">Session:</span>{" "}
                        <span data-testid="book-pet-summary-duration" className="text-slate-100 font-semibold">{petDuration} min</span>
                      </div>
                    )}
                    <div>
                      <span className="text-slate-500">Pets:</span>{" "}
                      <span data-testid="book-pet-summary-count" className="text-slate-100 font-semibold">{petCount}</span>
                    </div>
                    {petAddons.length > 0 && (
                      <div className="text-rose-300 font-mono text-xs" data-testid="book-pet-summary-addons">
                        + {petAddons.map((id) => PET_ADDON_META[id]?.label).filter(Boolean).join(", ")}
                      </div>
                    )}
                    <div className="text-rose-400 font-mono text-xs uppercase tracking-wider pt-1">In-home · one-time</div>
                  </>
                ) : isOutdoor ? (
                  <>
                    <div>
                      <span className="text-slate-500">Service:</span>{" "}
                      <span data-testid="book-outdoor-summary-service" className="text-slate-100 font-semibold">{outdoorSvcLabel}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Size:</span>{" "}
                      <span data-testid="book-outdoor-summary-size" className="text-slate-100 font-semibold">{outdoorSizeLabel}</span>
                    </div>
                    {outdoorAddons.length > 0 && (
                      <div className="text-emerald-300 font-mono text-xs" data-testid="book-outdoor-summary-addons">
                        + {outdoorAddons.map((id) => OUTDOOR_ADDON_META[id]?.label).filter(Boolean).join(", ")}
                      </div>
                    )}
                    <div className="text-emerald-400 font-mono text-xs uppercase tracking-wider pt-1">Exterior · one-time</div>
                  </>
                ) : isConv ? (
                  <>
                    <div>
                      <span className="text-slate-500">Service:</span>{" "}
                      <span data-testid="book-conv-summary-service" className="text-slate-100 font-semibold">{convSvcLabel}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Size:</span>{" "}
                      <span data-testid="book-conv-summary-size" className="text-slate-100 font-semibold">{convSizeLabel}</span>
                    </div>
                    {convAddons.length > 0 && (
                      <div className="text-violet-300 font-mono text-xs" data-testid="book-conv-summary-addons">
                        + {convAddons.map((id) => CONV_ADDON_META[id]?.label).filter(Boolean).join(", ")}
                      </div>
                    )}
                    <div className="text-violet-400 font-mono text-xs uppercase tracking-wider pt-1">Everyday help · one-time</div>
                  </>
                ) : bookingType === "home" ? (
                  <>
                    <div>{beds} bedroom{beds !== 1 ? "s" : ""} · {baths} bathroom{baths !== 1 ? "s" : ""}</div>
                    <div>{deep ? "Deep Clean (1.5×)" : "Standard cleaning"}</div>
                    {homeAddons.length > 0 && (
                      <div className="text-teal-400 font-mono text-xs" data-testid="book-home-summary-addons">
                        + {homeAddons.map((id) => HOME_ADDON_META[id]?.label || id).join(", ")}
                      </div>
                    )}
                    <div className="text-teal-400 font-mono text-xs uppercase tracking-wider pt-1">{FREQUENCIES.find(f => f.id === frequency)?.label}</div>
                  </>
                ) : (
                  <>
                    <div className="font-mono text-xs uppercase tracking-widest text-teal-400">Commercial · {SPACE_LABEL[spaceType]}</div>
                    <div>{SQFT_LABEL[sqft]}</div>
                    <div>{deep ? "Deep Clean (1.5×)" : "Standard cleaning"}</div>
                    {homeAddons.length > 0 && (
                      <div className="text-teal-400 font-mono text-xs" data-testid="book-business-summary-addons">
                        + {homeAddons.map((id) => BUSINESS_ADDON_META[id]?.label || id).join(", ")}
                      </div>
                    )}
                    <div className="text-teal-400 font-mono text-xs uppercase tracking-wider pt-1">{FREQUENCIES.find(f => f.id === frequency)?.label}</div>
                  </>
                )}
                {(street || city) && (
                  <div className="pt-2 text-xs text-slate-400">
                    <span className="text-slate-500">Location:</span> {[street, unit, city, stateField, postal].filter(Boolean).join(", ")}
                  </div>
                )}
                {scheduledAt && (
                  <div className="text-xs text-slate-400">
                    <span className="text-slate-500">When:</span>{" "}
                    <span data-testid="book-summary-schedule">{new Date(scheduledAt).toLocaleString()}</span>
                  </div>
                )}
              </div>
              <div className="mt-4 border-t border-slate-700 pt-4">
                <div className="text-xs text-slate-400">
                  {priceInfo.quote ? "Total" : (frequency === "one-time" || isCar || isPet || isOutdoor || isConv) ? "Total" : "Charged today (first visit)"}
                </div>
                <div className="font-display font-extrabold text-4xl mt-1" data-testid="book-total">
                  {priceInfo.quote ? "—" : formatPrice(price, (isPet || isOutdoor || isConv) ? "USD" : currency)}
                </div>
                <div className="text-xs font-mono text-slate-500 mt-1">
                  {priceInfo.quote
                    ? "Custom quote required"
                    : `Platform fee 20% (${formatPrice(price * 0.20, (isPet || isOutdoor || isConv) ? "USD" : currency)}) · Cleaner payout 80% (${formatPrice(price * 0.80, (isPet || isOutdoor || isConv) ? "USD" : currency)})`}
                </div>
              </div>
              <Button
                onClick={handleCheckout}
                disabled={loading || priceInfo.quote}
                data-testid="book-checkout-btn"
                size="lg"
                className={`w-full mt-6 font-bold h-12 ${
                  isCar ? "bg-amber-400 hover:bg-amber-300 text-slate-950" :
                  isPet ? "bg-rose-400 hover:bg-rose-300 text-slate-950" :
                  isOutdoor ? "bg-emerald-400 hover:bg-emerald-300 text-slate-950" :
                  isConv ? "bg-violet-400 hover:bg-violet-300 text-slate-950" :
                          "bg-teal-500 hover:bg-teal-600 text-slate-900"
                }`}
              >
                {loading ? "Processing..." : priceInfo.quote ? "Custom quote required" : "Pay with Stripe"}
              </Button>
              <div className="mt-4 space-y-2 text-xs text-slate-400">
                <div className="flex items-start gap-2"><Lock className="w-3.5 h-3.5 mt-0.5 text-teal-400 flex-shrink-0" /><span>Card authorized at booking; funds held by CLEANBAZ until service is marked complete.</span></div>
                <div className="flex items-start gap-2"><ShieldCheck className="w-3.5 h-3.5 mt-0.5 text-teal-400 flex-shrink-0" /><span>Released to cleaner only after 100% completion.</span></div>
                {!isCar && !isPet && !isOutdoor && !isConv && frequency !== "one-time" && <div className="flex items-start gap-2"><Repeat className="w-3.5 h-3.5 mt-0.5 text-teal-400 flex-shrink-0" /><span>Recurring plan · pause or cancel from your dashboard anytime.</span></div>}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
